/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React from 'react';
import { Card, Image } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';

export interface MapWidgetProps {
  center?: number[];
  zoom?: number;
  maxZoom?: number;
  accessToken: string;
  markers?: {
    id: string;
    name: string;
    latLng: number | any[];
    items?: {
      icon?: string;
      description: string;
    }[];
  };
}

const isValidUrl = (urlString: string) => {
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

const MapIcon: any = icon({
  iconUrl: require('./assets/marker.svg'),
  iconSize: [38, 95], // size of the icon
  shadowSize: [50, 64], // size of the shadow
  iconAnchor: [22, 94], // point of the icon which will correspond to marker's location
  shadowAnchor: [4, 62], // the same for the shadow
  popupAnchor: [-3, -76], // point from which the popup should open relative to the iconAnchor
});

export const MapWidget = ({
  center,
  zoom,
  maxZoom,
  accessToken,
  markers,
}: MapWidgetProps) => {
  const mapConfig: any = {
    maxZoom: maxZoom,
    id: 'mapbox/light-v10',
    url: 'https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}',
    attribution:
      'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
    accessToken: accessToken,
  };

  const _getIcon = (Icon: any) => {
    let _icon: any = [];
    if (typeof Icon === 'string') {
      if (isValidUrl(Icon)) {
        _icon.push(
          <Image
            key={`image-icon`}
            src={Icon}
            width={60}
            height={60}
            className="icon icon-xs icon-tertiary me-2"
          />
        );
      } else {
        const HeroIcon = require('@heroicons/react/solid');
        const NavIcon = HeroIcon[Icon];
        if (NavIcon) {
          _icon.push(
            <NavIcon
              key={`hero-icon`}
              src={Icon}
              className="icon icon-xs icon-tertiary me-2"
            />
          );
        }
      }
      return _icon;
    }
    if (Icon) {
      _icon.push(
        <Icon key={`hero-icon-2`} className="icon icon-xs icon-tertiary me-2" />
      );
    }
    return _icon;
  };

  const _renderMarkerItem = (
    items?: {
      icon?: string;
      description: string;
    }[]
  ) => {
    const comp: any = [];
    if (items && Array.isArray(items)) {
      items.forEach((item, index) => {
        comp.push(
          <div
            key={`popup-item-${index}`}
            className="d-flex align-items-center"
          >
            {item.icon && _getIcon(item.icon)}
            <div className="font-xs text-dark">{item.description}</div>
          </div>
        );
      });
    }
    return comp;
  };

  const _renderMarkers = () => {
    let _comp: any = [];
    if (markers && Array.isArray(markers)) {
      for (const marker of markers) {
        _comp.push(
          <Marker
            key={`map-marker-${marker.id}`}
            position={marker.latLng}
            icon={MapIcon}
          >
            <Popup>
              <Card className="card-article-wide border-0 flex-column no-gutters no-hover">
                <Card.Body className="py-0 d-flex flex-column justify-content-between col-12">
                  <h5 className="fw-normal mb-2">{marker.name}</h5>
                  {_renderMarkerItem(marker.items)}
                </Card.Body>
              </Card>
            </Popup>
          </Marker>
        );
      }
    }
    return _comp;
  };
  return (
    <MapContainer id="mapbox" className="p-4" center={center} zoom={zoom}>
      <TileLayer {...mapConfig} />
      {_renderMarkers()}
    </MapContainer>
  );
};

MapWidget.defaultProps = {
  center: [37.57, -122.26],
  zoom: 3,
  maxZoom: 18,
};
